import * as React from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { FormikErrors, FormikProvider } from 'formik'
import { UniversalExportsExporterMetadataInput } from 'generated/resources/graphql'
import { sortBy } from 'lodash'

import Loading from 'modules/core/components/Loading'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  useConfiguration,
  useCreateConfiguration,
  useUpdateConfiguration,
} from 'modules/universal_exports/hooks/useConfiguration'
import { useCreateExport, useExporter } from 'modules/universal_exports/hooks/useExporter'
import { requiresOptions } from 'modules/universal_exports/lib/exporter'

import { Content } from './_Content'
import { ConfigurationFormValues, FormExtraOption, FormField, FormFilter } from './types'

interface Props {
  resource: string
  overrideFilters?: Record<string, Array<number | string | boolean>>
  metadata?: Record<string, Array<number | string | boolean>>
  hasDynamicFields?: boolean
  fieldsTitleTooltip?: string
  onClose: () => void
  onExport?: () => void
}

const toDefaultField = ({ name, label }: { name: string; label?: string | null }) => ({
  name: name,
  label: label ?? '',
})

export const ExportSidepanel = ({
  resource,
  overrideFilters = {},
  metadata = {},
  hasDynamicFields = false,
  fieldsTitleTooltip,
  onClose,
  onExport,
}: Props) => {
  const { company, access } = useCurrentSession()

  const exporterMetadata = useUnsafeMemo(() => {
    const formatedMetadata: UniversalExportsExporterMetadataInput[] = []
    Object.entries(metadata).forEach(([name, values]) => {
      const element = formatedMetadata.find((e) => e.name === name)
      const normalizedValues = values.map(String)

      if (normalizedValues.length === 0) return

      if (element) {
        element.values = normalizedValues
      } else {
        formatedMetadata.push({ name, values: normalizedValues })
      }
    })

    return formatedMetadata
  }, [metadata])

  const exporter = useExporter({
    resource,
    companyId: company.id,
    metadata: exporterMetadata,
  })
  const configuration = useConfiguration({ resource }, hasDynamicFields)

  const { create: createConfiguration, loading: creatingConfiguration } = useCreateConfiguration()
  const { update: updateConfiguration, loading: updatingConfiguration } = useUpdateConfiguration()
  const { create: createExport, loading: exporting } = useCreateExport()

  const loading = exporter.loading || configuration.loading

  // Refresh exporter data when payroll run filter changes to get updated dynamic fields
  const handleFilterChange = React.useCallback(() => {
    // For payroll run employee compensations exporter, refresh when payroll run filter changes
    if (hasDynamicFields) {
      exporter.refetch?.()
    }
  }, [hasDynamicFields, exporter])

  const defaultExtraOptions: FormExtraOption[] = useUnsafeMemo(
    () =>
      exporter.extraOptions.map(({ name }) => {
        const value = configuration.extraOptions.find((eo) => eo.name == name)?.value ?? false
        return { name, value }
      }),
    [configuration.extraOptions, exporter.extraOptions]
  )

  const defaultFields: FormField[] = useUnsafeMemo(() => {
    if (configuration.id) {
      return configuration.fields
        .filter((field) => exporter.fields.some((f) => f.name === field.name))
        .map(toDefaultField)
    } else {
      return sortBy(
        exporter.fields.filter((field) => field.default),
        ['defaultPosition']
      ).map(toDefaultField)
    }
  }, [configuration.id, configuration.fields, exporter.fields])

  const defaultFilters: FormFilter[] = useUnsafeMemo(() => {
    let filters: FormFilter[] = []

    if (configuration.id) {
      filters = configuration.filters
        .filter((filter) => exporter.filters.some((f) => f.name === filter.name))
        .map(({ name, values }) => {
          const exporterFilter = exporter.filters.find((f) => f.name === name)

          const validValues =
            exporterFilter && requiresOptions(exporterFilter.kind)
              ? values.filter((value) => exporterFilter.options?.some((o) => o.value === value))
              : values

          return { name, values: validValues }
        })
    } else {
      filters = exporter.filters
        .filter((filter) => filter.default)
        .map(({ name }) => ({ name, values: [] }))
    }

    Object.entries(overrideFilters).forEach(([name, values]) => {
      const filter = filters.find((f) => f.name === name)
      const normalizedValues = values.map(String)

      if (normalizedValues.length === 0) return

      if (filter) {
        filter.values = normalizedValues
      } else {
        filters.push({ name, values: normalizedValues })
      }
    })

    return filters
  }, [configuration.id, configuration.filters, exporter.filters, overrideFilters])

  const defaultGroupColumn: string | null | undefined = useUnsafeMemo(() => {
    if (configuration.id) {
      if (exporter.groupableColumns.some((f) => f.name === configuration.groupColumn)) {
        return configuration.groupColumn
      }
    }
    return null
  }, [configuration.id, configuration.groupColumn, exporter.groupableColumns])

  const buildConfigurationData = (data: ConfigurationFormValues) => ({
    resource: data.resource,
    format: data.format,
    filters: data.filters.filter((filter) => filter.values.length > 0),
    extraOptions: data.extraOptions,
    fields: data.fields.map(({ name, label }, index) => ({
      name,
      label: label || null,
      position: index + 1,
    })),
    groupColumn: data.groupColumn,
  })

  const upsertConfiguration = async (
    data: ConfigurationFormValues,
    setErrors: (errors: FormikErrors<ConfigurationFormValues>) => void
  ) => {
    if (data.id) {
      return handleResult(
        await updateConfiguration({
          ...buildConfigurationData(data),
          id: data.id,
        }),
        (errors) => setErrors(errors)
      )
    }

    return handleResult(
      await createConfiguration({
        ...buildConfigurationData(data),
        accessId: data.accessId,
        companyId: data.companyId,
      }),
      (errors) => setErrors(errors)
    )
  }

  const form = useForm<ConfigurationFormValues>({
    enableReinitialize: true,
    defaultValues: {
      id: configuration.id,
      accessId: access?.id,
      companyId: company?.id,
      format: configuration.format ?? 'xlsx',
      fields: defaultFields,
      extraOptions: defaultExtraOptions,
      filters: defaultFilters,
      resource,
      groupColumn: defaultGroupColumn,
    },
    validate: (data) => {
      const errors: FormErrors<ConfigurationFormValues> = {}

      if (!data.format) {
        errors.format = i18n.t('common.empty')
      }

      if (!data.fields || data.fields.length === 0) {
        errors.fields = i18n.t('common.empty')
      }

      if (
        data.groupColumn &&
        data.fields.length > 0 &&
        !data.fields.some((f) => f.name === data.groupColumn)
      ) {
        errors.groupColumn = i18n.t('universal_exports.group_column.error_column_not_selected_v2')
      }

      return errors
    },
    onSubmit: async (data: ConfigurationFormValues, { setErrors }) => {
      const handledConfiguration = await upsertConfiguration(data, setErrors)
      if (!handledConfiguration?.success) return { success: false, errors: [] }

      const configurationId = handledConfiguration.data.configuration?.id
      if (!configurationId) return handledConfiguration

      const handledExport = handleResult(await createExport({ configurationId }), (errors) =>
        setErrors(errors)
      )
      if (!handledExport.success) return handledExport

      track(trackingEvents.universalExport.submitUniversalExport, {
        resource: resource,
        format: data.format,
      })

      app.setFlash({
        type: 'correct',
        message: i18n.t('universal_exports.sidepanel.export_success_v2'),
      })

      onClose()
      onExport?.()
      return handledExport
    },
  })

  const isSubmitDisabled =
    !form.isValid || loading || creatingConfiguration || updatingConfiguration || exporting

  const SidebarTitle = (
    <Box alignItems="center" justifyContent="center" flexDirection="row" flexGrow>
      <Text.Normal weight="semibold">
        {i18n.t('universal_exports.sidepanel.title_v2', { resource: exporter.label })}
      </Text.Normal>
    </Box>
  )

  const FooterActions = (
    <Box flexDirection="row" gap="s8" paddingX="s8" justifyContent="flexEnd">
      <Button type="secondary" onClick={onClose}>
        {i18n.t('common.cancel')}
      </Button>
      <Button type="primary" onClick={() => form.handleSubmit()} disabled={isSubmitDisabled} submit>
        {i18n.t('common.export')}
      </Button>
    </Box>
  )

  if (loading) {
    return (
      <Sidepanel onClose={onClose} title={SidebarTitle}>
        <Loading />
      </Sidepanel>
    )
  }

  return (
    <Sidepanel
      title={loading ? null : SidebarTitle}
      footer={loading ? null : FooterActions}
      width="wide"
      closeLabel={i18n.t('common.close')}
      onClose={onClose}
      overlay
      boxPadding
      open
      headerBorder
      locked
    >
      {loading ? (
        <Loading />
      ) : (
        <FormikProvider value={form}>
          <Content
            fields={exporter.fields}
            filters={exporter.filters}
            extraOptions={exporter.extraOptions}
            groupableColumns={exporter.groupableColumns}
            overrideFilters={overrideFilters}
            onFilterChange={handleFilterChange}
            fieldsTitleTooltip={fieldsTitleTooltip}
          />
        </FormikProvider>
      )}
    </Sidepanel>
  )
}
